import { MAX_ENTRIES } from './limits';

/**
 * Entry weight rules, shared by the entries panel, the wheel, and the
 * import/export paths so all three agree on what a weight means.
 *
 * A weight is the share of the wheel an entry occupies, relative to the others.
 * Two entries at 1 and 3 split the wheel 25/75. A weight of 0 makes the entry
 * inactive: it stays in the list, greyed out, but takes no slice and can never
 * be selected.
 */

export const MIN_WEIGHT = 0;
export const MAX_WEIGHT = 1000;
export const DEFAULT_WEIGHT = 1;
/** Two decimals is plenty for odds, and keeps 0.1 + 0.2 out of the UI. */
const WEIGHT_DECIMALS = 2;

/** Clamp any user or file input into a valid weight. Invalid input becomes 1. */
export function clampWeight(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
  if (!Number.isFinite(n)) return DEFAULT_WEIGHT;
  const clamped = Math.min(MAX_WEIGHT, Math.max(MIN_WEIGHT, n));
  return Math.round(clamped * 10 ** WEIGHT_DECIMALS) / 10 ** WEIGHT_DECIMALS;
}

/** "2" not "2.00"; "0.5" stays "0.5". */
export function formatWeight(w: number): string {
  return String(clampWeight(w));
}

/** True when nothing has been customised — the wheel is a plain equal split. */
export function isUniform(weights: number[]): boolean {
  if (weights.length === 0) return true;
  const first = weights[0];
  return weights.every(w => w === first);
}

/** True when the entry takes a slice and can be selected. */
export function isActive(weight: number): boolean {
  return Number.isFinite(weight) && weight > 0;
}

export function totalWeight(weights: number[]): number {
  return weights.reduce((sum, w) => sum + (isActive(w) ? w : 0), 0);
}

/**
 * Selection probability per entry, as a percentage. Inactive entries get 0.
 * Returns an all-zero array when nothing is selectable.
 */
export function probabilities(weights: number[]): number[] {
  const total = totalWeight(weights);
  if (total <= 0) return weights.map(() => 0);
  return weights.map(w => (isActive(w) ? (w / total) * 100 : 0));
}

// ── Bulk Edit text format ────────────────────────────────────────────────────

/**
 * Parse one Bulk Edit line into a name and a weight.
 *
 * Three suffix forms are accepted, all equivalent:
 *   Alice::2.5    the documented format
 *   Alice x3      what this app has always written, kept so pasting back a
 *                 previously exported list still round-trips
 *   Alice (3)     a common way people already annotate lists by hand
 *
 * A line with no suffix gets weight 1. `::` is checked first because a name
 * containing " x2" is far more likely than one containing "::".
 */
export function parseBulkLine(line: string): { name: string; weight: number } {
  const trimmed = line.trim();

  const colon = trimmed.match(/^(.*?)\s*::\s*(-?\d*\.?\d+)\s*$/);
  if (colon && colon[1].trim()) return { name: colon[1].trim(), weight: clampWeight(colon[2]) };

  const paren = trimmed.match(/^(.+?)\s*\((-?\d*\.?\d+)\)\s*$/);
  if (paren) return { name: paren[1].trim(), weight: clampWeight(paren[2]) };

  const x = trimmed.match(/^(.+?)\s*[x×]\s*(-?\d*\.?\d+)\s*$/i);
  if (x) return { name: x[1].trim(), weight: clampWeight(x[2]) };

  return { name: trimmed, weight: DEFAULT_WEIGHT };
}

/**
 * Render a name and weight back to a Bulk Edit line.
 *
 * `x2` rather than `::2` on purpose: it is what previous versions wrote, so
 * text people already have saved keeps its meaning, and it reads better. Both
 * forms parse, so nothing is lost by choosing one to emit.
 */
export function serializeBulkLine(name: string, weight: number): string {
  const w = clampWeight(weight);
  return w === DEFAULT_WEIGHT ? name : `${name} x${formatWeight(w)}`;
}

export function serializeBulk(names: string[], weights: number[]): string {
  return names.map((n, i) => serializeBulkLine(n, weights[i] ?? DEFAULT_WEIGHT)).join('\n');
}

// ── CSV export / import ──────────────────────────────────────────────────────

/** Quote a CSV field only when it needs it. */
function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Build the export payload.
 *
 * Emits a bare one-name-per-line list while every weight is 1 — the format
 * this app has always produced, and the one every other tool can read. Only
 * once weights are actually in use does it switch to two-column CSV, so a
 * plain list never gains a header it did not need.
 */
export function buildExport(names: string[], weights: number[]): {
  content: string;
  filename: string;
  mime: string;
} {
  const allDefault = names.every((_, i) => (weights[i] ?? DEFAULT_WEIGHT) === DEFAULT_WEIGHT);
  if (allDefault) {
    return { content: names.join('\n'), filename: 'wheel-entries.txt', mime: 'text/plain' };
  }
  const rows = names.map((n, i) => `${csvField(n)},${formatWeight(weights[i] ?? DEFAULT_WEIGHT)}`);
  return {
    content: ['name,weight', ...rows].join('\n'),
    filename: 'wheel-entries.csv',
    mime: 'text/csv',
  };
}

/** Split a single CSV line, honouring double-quoted fields. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') { field += '"'; i++; }
        else quoted = false;
      } else field += ch;
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      out.push(field);
      field = '';
    } else field += ch;
  }
  out.push(field);
  return out.map(f => f.trim());
}

/**
 * Parse an imported file into weighted entries.
 *
 * Handles both shapes:
 *   - two-column CSV with a `name,weight` header (what buildExport writes)
 *   - anything else: names split on newlines, commas, tabs, and semicolons,
 *     each at weight 1 — the long-standing behaviour, unchanged
 */
export function parseImport(text: string): { name: string; weight: number }[] {
  const lines = text.split(/\r?\n/);
  const header = lines.find(l => l.trim())?.trim() ?? '';
  const headerCols = splitCsvLine(header).map(c => c.toLowerCase());

  if (headerCols.length >= 2 && headerCols[0] === 'name' && headerCols[1] === 'weight') {
    const start = lines.indexOf(lines.find(l => l.trim()) as string) + 1;
    return lines
      .slice(start)
      .map(l => splitCsvLine(l))
      .filter(cols => cols[0])
      .slice(0, MAX_ENTRIES)
      .map(cols => ({ name: cols[0], weight: clampWeight(cols[1]) }));
  }

  return text
    .split(/\r?\n|,|\t|;/)
    .map(s => s.trim().replace(/^["']|["']$/g, ''))
    .filter(Boolean)
    .slice(0, MAX_ENTRIES)
    .map(name => ({ name, weight: DEFAULT_WEIGHT }));
}
